import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { API_URL } from '../../utils/constants';

function UsersScreen() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetting, setResetting] = useState({});
  const [resetError, setResetError] = useState({});
  const [updatingRole, setUpdatingRole] = useState({});
  const [roleError, setRoleError] = useState({});
  const [showTempPasswordModal, setShowTempPasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [settingTempPassword, setSettingTempPassword] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    userRole: 'user',
    tempPassword: ''
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [addUserErrors, setAddUserErrors] = useState({});

  useEffect(() => {
    async function fetchUsers() {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        // Check if response is JSON
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Server error: Backend server may not be running. Please check that your backend is running on port 5000.');
        }
        
        if (!res.ok) throw new Error('Failed to fetch users');
        const data = await res.json();
        setUsers(data.users || data); // support both {users:[]} and []
      } catch (err) {
        console.error('Fetch users error:', err);
        setError(err.message || 'Error fetching users');
      }
      setLoading(false);
    }
    fetchUsers();
  }, []);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const filtered = users.filter(u => {
    const fullName = `${u.first_name || ''} ${u.last_name || ''}`.trim();
    return fullName.toLowerCase().includes(search.toLowerCase()) || u.email?.toLowerCase().includes(search.toLowerCase());
  });

  const handleResetPassword = (user) => {
    setSelectedUser(user);
    setTempPassword('');
    setShowTempPasswordModal(true);
  };

  const handleSetTempPassword = async () => {
    if (!tempPassword || tempPassword.length < 6) {
      alert('Temporary password must be at least 6 characters.');
      return;
    }
    
    setSettingTempPassword(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/users/${selectedUser.id}/temp-password`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempPassword })
      });
      
      if (!res.ok) throw new Error('Failed to set temporary password');
      
      alert('Temporary password set successfully! User will be required to change it on next login.');
      setShowTempPasswordModal(false);
      setSelectedUser(null);
      setTempPassword('');
    } catch (err) {
      alert(err.message || 'Error setting temporary password');
    }
    setSettingTempPassword(false);
  };

  const handleAddUser = () => {
    setNewUser({
      firstName: '',
      lastName: '',
      company: '',
      email: '',
      userRole: 'user',
      tempPassword: ''
    });
    setAddUserErrors({});
    setShowAddUserModal(true);
  };

  const handleCreateUser = async () => {
    // Validate form
    let errs = {};
    if (!newUser.firstName) errs.firstName = 'First name is required.';
    if (!newUser.lastName) errs.lastName = 'Last name is required.';
    if (!newUser.email) errs.email = 'Email is required.';
    else if (!validateEmail(newUser.email)) errs.email = 'Please enter a valid email address.';
    if (!newUser.tempPassword) errs.tempPassword = 'Temporary password is required.';
    else if (newUser.tempPassword.length < 6) errs.tempPassword = 'Temporary password must be at least 6 characters.';
    
    setAddUserErrors(errs);
    if (Object.keys(errs).length > 0) return;
    
    setCreatingUser(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser)
      });
      
      // Check if response is JSON
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Server error: Backend server may not be running. Please check that your backend is running on port 5000.');
      }
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to create user');
      
      // Add new user to the list
      setUsers(prevUsers => [...prevUsers, data.user]);
      
      alert('User created successfully! They will be required to change their password on first login.');
      setShowAddUserModal(false);
      setNewUser({
        firstName: '',
        lastName: '',
        company: '',
        email: '',
        userRole: 'user',
        tempPassword: ''
      });
    } catch (err) {
      console.error('Create user error:', err);
      alert(err.message || 'Error creating user');
    }
    setCreatingUser(false);
  };

  const handleRoleChange = async (userId, newRole) => {
    setUpdatingRole(r => ({ ...r, [userId]: true }));
    setRoleError(e => ({ ...e, [userId]: null }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ userRole: newRole })
      });
      if (!res.ok) throw new Error('Failed to update role');
      
      // Update the user in the local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId ? { ...user, userRole: newRole } : user
        )
      );
      
      alert('User role updated successfully!');
    } catch (err) {
      setRoleError(e => ({ ...e, [userId]: err.message || 'Error updating role' }));
    }
    setUpdatingRole(r => ({ ...r, [userId]: false }));
  };

  return (
    <>
      <section style={{ 
        background: 'var(--primary-color)', 
        borderBottom: '2px solid var(--secondary-color)', 
        padding: '1rem 0',
        marginBottom: '2rem'
      }}>
        <Container>
          <h2 style={{ 
            color: 'var(--accent-color)', 
            fontFamily: 'Orbitron, Audiowide, cursive', 
            fontWeight: 700, 
            fontSize: '1.5rem',
            margin: 0
          }}>
            Manage Users
          </h2>
        </Container>
      </section>
      <section className="dashboard-section">
        <Container>
          <div className="dashboard-card">
            <div className="mb-4 d-flex justify-content-between align-items-center">
              <input
                type="text"
                className="form-control"
                placeholder="Search by user name or email..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ maxWidth: 350, display: 'inline-block' }}
              />
              <button
                onClick={handleAddUser}
                className="btn btn-success"
                style={{ 
                  fontSize: '0.875rem', 
                  padding: '0.5rem 1rem',
                  fontFamily: 'Orbitron, Audiowide, cursive',
                  fontWeight: 600
                }}
              >
                + Add User
              </button>
            </div>
            {error && <div className="alert alert-danger">{error}</div>}
            {loading ? (
              <div>Loading users...</div>
            ) : (
              <table className="table table-dark table-striped table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>User Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(user => (
                    <tr key={user.id} style={{ 
                      opacity: user.userRole === 'disabled' ? 0.6 : 1,
                      backgroundColor: user.userRole === 'disabled' ? 'rgba(220, 53, 69, 0.1)' : 'transparent'
                    }}>
                      <td>{`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}</td>
                      <td>{user.email}</td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={user.userRole || 'user'}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          disabled={updatingRole[user.id]}
                          style={{ 
                            background: user.userRole === 'disabled' ? '#dc3545' : 'var(--primary-color)', 
                            color: 'var(--accent-color)', 
                            border: '1px solid var(--secondary-color)',
                            maxWidth: '120px'
                          }}
                        >
                          <option value="user">User</option>
                          <option value="guest">Guest</option>
                          <option value="venue">Venue</option>
                          <option value="artist">Artist</option>
                          <option value="admin">Admin</option>
                          <option value="disabled">Disabled</option>
                        </select>
                        {updatingRole[user.id] && <div className="text-info small mt-1">Updating...</div>}
                        {roleError[user.id] && <div className="text-danger small mt-1">{roleError[user.id]}</div>}
                      </td>
                      <td>
                        <span style={{ 
                          color: 'var(--accent-color)', 
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}>
                          {user.userRole || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <button
                          className="btn btn-primary"
                          onClick={() => handleResetPassword(user)}
                          style={{ 
                            fontSize: '0.75rem', 
                            padding: '0.25rem 0.5rem',
                            lineHeight: '1.2'
                          }}
                        >
                          Set Temp Password
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Container>
      </section>

      {/* Temporary Password Modal */}
      {showTempPasswordModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--primary-color)',
            border: '2px solid var(--accent-color)',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
            color: 'var(--accent-color)'
          }}>
            <h3 style={{ marginBottom: '1rem', fontFamily: 'Orbitron, Audiowide, cursive' }}>
              Set Temporary Password
            </h3>
            <p style={{ marginBottom: '1rem', color: 'var(--secondary-color)' }}>
              Set a temporary password for {selectedUser?.first_name} {selectedUser?.last_name} ({selectedUser?.email})
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Temporary Password:
              </label>
              <input
                type="text"
                value={tempPassword}
                onChange={(e) => setTempPassword(e.target.value)}
                placeholder="Enter temporary password (min 6 chars)"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: 'var(--primary-color)',
                  border: '1px solid var(--secondary-color)',
                  color: 'var(--accent-color)',
                  borderRadius: '4px'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowTempPasswordModal(false);
                  setSelectedUser(null);
                  setTempPassword('');
                }}
                style={{
                  background: 'none',
                  border: '1px solid var(--secondary-color)',
                  color: 'var(--secondary-color)',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSetTempPassword}
                disabled={settingTempPassword}
                style={{
                  background: 'var(--accent-color)',
                  border: '1px solid var(--accent-color)',
                  color: 'var(--primary-color)',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                {settingTempPassword ? 'Setting...' : 'Set Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddUserModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--primary-color)',
            border: '2px solid var(--accent-color)',
            borderRadius: '8px',
            padding: '2rem',
            maxWidth: '500px',
            width: '90%',
            color: 'var(--accent-color)',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ marginBottom: '1rem', fontFamily: 'Orbitron, Audiowide, cursive' }}>
              Add New User
            </h3>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                First Name: *
              </label>
              <input
                type="text"
                value={newUser.firstName}
                onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                placeholder="Enter first name"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: 'var(--primary-color)',
                  border: `1px solid ${addUserErrors.firstName ? '#dc3545' : 'var(--secondary-color)'}`,
                  color: 'var(--accent-color)',
                  borderRadius: '4px'
                }}
              />
              {addUserErrors.firstName && <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>{addUserErrors.firstName}</div>}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Last Name: *
              </label>
              <input
                type="text"
                value={newUser.lastName}
                onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                placeholder="Enter last name"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: 'var(--primary-color)',
                  border: `1px solid ${addUserErrors.lastName ? '#dc3545' : 'var(--secondary-color)'}`,
                  color: 'var(--accent-color)',
                  borderRadius: '4px'
                }}
              />
              {addUserErrors.lastName && <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>{addUserErrors.lastName}</div>}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Company:
              </label>
              <input
                type="text"
                value={newUser.company}
                onChange={(e) => setNewUser({...newUser, company: e.target.value})}
                placeholder="Enter company (optional)"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: 'var(--primary-color)',
                  border: '1px solid var(--secondary-color)',
                  color: 'var(--accent-color)',
                  borderRadius: '4px'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Email: *
              </label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="Enter email address"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: 'var(--primary-color)',
                  border: `1px solid ${addUserErrors.email ? '#dc3545' : 'var(--secondary-color)'}`,
                  color: 'var(--accent-color)',
                  borderRadius: '4px'
                }}
              />
              {addUserErrors.email && <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>{addUserErrors.email}</div>}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Role: *
              </label>
              <select
                value={newUser.userRole}
                onChange={(e) => setNewUser({...newUser, userRole: e.target.value})}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: 'var(--primary-color)',
                  border: '1px solid var(--secondary-color)',
                  color: 'var(--accent-color)',
                  borderRadius: '4px'
                }}
              >
                <option value="user">User</option>
                <option value="guest">Guest</option>
                <option value="venue">Venue</option>
                <option value="artist">Artist</option>
                <option value="admin">Admin</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Temporary Password: *
              </label>
              <input
                type="text"
                value={newUser.tempPassword}
                onChange={(e) => setNewUser({...newUser, tempPassword: e.target.value})}
                placeholder="Enter temporary password (min 6 chars)"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  background: 'var(--primary-color)',
                  border: `1px solid ${addUserErrors.tempPassword ? '#dc3545' : 'var(--secondary-color)'}`,
                  color: 'var(--accent-color)',
                  borderRadius: '4px'
                }}
              />
              {addUserErrors.tempPassword && <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>{addUserErrors.tempPassword}</div>}
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddUserModal(false);
                  setNewUser({
                    firstName: '',
                    lastName: '',
                    company: '',
                    email: '',
                    userRole: 'user',
                    tempPassword: ''
                  });
                  setAddUserErrors({});
                }}
                style={{
                  background: 'none',
                  border: '1px solid var(--secondary-color)',
                  color: 'var(--secondary-color)',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={creatingUser}
                style={{
                  background: 'var(--accent-color)',
                  border: '1px solid var(--accent-color)',
                  color: 'var(--primary-color)',
                  padding: '0.5rem 1rem',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                {creatingUser ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default UsersScreen; 